import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import {
  Box,
  Button,
  Grid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { CherryPayApi } from "../../api/models";

export interface AdvancedFilterOption {
  id: string;
  label: string;
  description?: string;
}

interface FilterSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  advancedFilters: AdvancedFilterOption[];
  extendedFields: CherryPayApi.ExtendedFieldDefinition[];
  selectedFilters: Set<string>;
  onConfirm: (selectedFilters: Set<string>) => void;
}

export const FilterSelectionDialog = ({
  isOpen,
  onClose,
  advancedFilters,
  extendedFields,
  selectedFilters,
  onConfirm,
}: FilterSelectionDialogProps) => {
  const [localSelectedFilters, setLocalSelectedFilters] = useState<Set<string>>(
    new Set(selectedFilters)
  );

  // Reset local selection when dialog opens
  useEffect(() => {
    if (isOpen) {
      setLocalSelectedFilters(new Set(selectedFilters));
    }
  }, [isOpen, selectedFilters]);

  const toggleFilter = (filterId: string) => {
    setLocalSelectedFilters((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(filterId)) {
        newSet.delete(filterId);
      } else {
        newSet.add(filterId);
      }
      return newSet;
    });
  };

  const handleConfirm = () => {
    onConfirm(localSelectedFilters);
    onClose();
  };

  const handleClose = () => {
    // Reset to original selection
    setLocalSelectedFilters(new Set(selectedFilters));
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Filters</ModalHeader>
        <ModalBody>
          <Tabs>
            <TabList>
              <Tab>Advanced Filters</Tab>
              <Tab>Extended Fields</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <Grid
                  templateColumns="repeat(auto-fill, minmax(200px, 1fr))"
                  gap={4}
                  py={4}
                >
                  {advancedFilters.map((filter) => (
                    <Box
                      key={filter.id}
                      p={4}
                      border="2px solid"
                      borderColor={
                        localSelectedFilters.has(filter.id)
                          ? "blue.500"
                          : "gray.200"
                      }
                      borderRadius="md"
                      cursor="pointer"
                      onClick={() => toggleFilter(filter.id)}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      textAlign="center"
                      minHeight="80px"
                      bg={
                        localSelectedFilters.has(filter.id)
                          ? "blue.50"
                          : "white"
                      }
                      _hover={{
                        borderColor: localSelectedFilters.has(filter.id)
                          ? "blue.600"
                          : "gray.300",
                        bg: localSelectedFilters.has(filter.id)
                          ? "blue.100"
                          : "gray.50",
                      }}
                      transition="all 0.2s"
                    >
                      <Text fontWeight="medium">{filter.label}</Text>
                    </Box>
                  ))}
                </Grid>
              </TabPanel>

              <TabPanel>
                {extendedFields.length === 0 ? (
                  <Box py={8} textAlign="center">
                    <Text>No Extended Fields found</Text>
                  </Box>
                ) : (
                  <Grid
                    templateColumns="repeat(auto-fill, minmax(200px, 1fr))"
                    gap={4}
                    py={4}
                  >
                    {extendedFields
                      .sort((a, b) => (a.FieldOrder ?? 0) - (b.FieldOrder ?? 0))
                      .map((field) => {
                        return (
                          <Box
                            key={field.id}
                            p={4}
                            border="2px solid"
                            borderColor={
                              localSelectedFilters.has(field.id)
                                ? "blue.500"
                                : "gray.200"
                            }
                            borderRadius="md"
                            cursor="pointer"
                            onClick={() => toggleFilter(field.id)}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            textAlign="center"
                            minHeight="80px"
                            bg={
                              localSelectedFilters.has(field.id)
                                ? "blue.50"
                                : "white"
                            }
                            _hover={{
                              borderColor: localSelectedFilters.has(field.id)
                                ? "blue.600"
                                : "gray.300",
                              bg: localSelectedFilters.has(field.id)
                                ? "blue.100"
                                : "gray.50",
                            }}
                            transition="all 0.2s"
                          >
                            <Text fontWeight="medium">
                              {field.FieldName || field.id}
                            </Text>
                          </Box>
                        );
                      })}
                  </Grid>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <Button variant="solid" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button colorScheme="cherryButton" onClick={handleConfirm}>
            Confirm
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
